import { useState, useEffect } from 'react';
import _ from 'lodash';

import { RedashOverviewDashboard } from '@scale/shared/types/endpoints/customer/overview';
import { SelfServeSupportedAttachmentTypes, UseCaseType } from '@scale/shared/defaults';
import { TaskType, TaskPipelineName } from '@scale/shared-types';
import { TextCollectionAttachmentType } from '@scale/shared/types/params/textcollection';
import { UploadedAsset } from './types';
import { DashboardPageType } from '@scale/shared/selfServe/types';

import queryString from 'query-string';
import { useHistory } from 'react-router';
import { Project } from 'dashboard/types';
import { isStudioProject, isRapidProject } from 'react-scale/studio/helpers';
import { IUserJson } from '@scale/shared/types/user';
import API from '@scale/frontend-lib/dashboard-api';

export const HOURS_PER_DAY = 24;
export const MIN_PER_HOUR = 60;
export const DATE_FORMAT = 'MMM DD, YYYY';

interface IBatchInfo {
  status: string | undefined;
  etaMins: number | undefined;
  completedAttempts: number | undefined;
}

export interface IHighLevelTaskStats {
  batches?: Record<string, IBatchInfo>;
}

/**
 * compute and return high level stats on a project's tasks (and workers)
 * @param data results from API.selfserve.fetchMetrics() using metricsType 'tasks'
 */
export const computeTaskStats = (data: RedashOverviewDashboard): IHighLevelTaskStats => {
  const stats: IHighLevelTaskStats = {
    batches: {},
  };

  // read stats about tasks / batches
  const batchData = data.widgets.filter(item => {
    return (
      item.visualization?.type === 'TABLE' &&
      item.visualization?.query?.name.toLowerCase().includes('batch stats')
    );
  });
  if (batchData.length > 0 && batchData[0].visualization?.query?.data?.rows) {
    // store eta of each batch
    batchData[0].visualization.query.data.rows.forEach(row => {
      const id = _.get(row, 'BATCH_ID');
      if (id && stats.batches) {
        stats.batches[id] = {
          status: _.get(row, 'BATCH_STATUS') || undefined,
          etaMins: _.get(row, 'ETA_MINS') || undefined,
          completedAttempts: _.get(row, 'COMPLETED_ATTEMPTS') || undefined,
        };
      }
    });
  }

  return stats;
};

export const getUploadedAssetMetadataObj = (uploadedAsset: UploadedAsset): any => {
  if (typeof uploadedAsset.metadata === 'string') {
    return JSON.parse(uploadedAsset.metadata);
  } else if (typeof uploadedAsset.metadata === 'object') {
    return uploadedAsset.metadata;
  }

  return null;
};

export const uploadedAssetToAttachmentType = (uploadedAsset: UploadedAsset): string | null => {
  const metadata = getUploadedAssetMetadataObj(uploadedAsset);
  if (metadata?.attachment_type) {
    return metadata.attachment_type;
  }

  if (uploadedAsset.mimeType.match('image/*')) {
    return SelfServeSupportedAttachmentTypes.Image;
  } else if (uploadedAsset.mimeType.match('audio/*')) {
    return SelfServeSupportedAttachmentTypes.Audio;
  } else if (uploadedAsset.mimeType === 'text/json') {
    return SelfServeSupportedAttachmentTypes.Json;
  } else if (uploadedAsset.mimeType.match('text/*') && uploadedAsset.text === undefined) {
    return SelfServeSupportedAttachmentTypes.DownloadableText;
  } else if (uploadedAsset.mimeType.match('text/*')) {
    return SelfServeSupportedAttachmentTypes.Text;
  } else if (uploadedAsset.mimeType.match('video/*')) {
    return SelfServeSupportedAttachmentTypes.Video;
  } else if (uploadedAsset.mimeType === 'application/pdf') {
    return SelfServeSupportedAttachmentTypes.Pdf;
  } else if (uploadedAsset.mimeType === 'unprocessed') {
    return 'unprocessed';
  } else {
    return null;
  }
};

export const attachmentTypeToSelectableProjectTypes = (
  attachmentType: SelfServeSupportedAttachmentTypes,
  isCoveredByHipaa?: boolean,
): TaskType[] | null => {
  switch (attachmentType) {
    case SelfServeSupportedAttachmentTypes.Image:
      return [
        TaskType.ImageAnnotation,
        TaskType.SegmentAnnotation,
        TaskType.TextCollection,
        TaskType.DocumentTranscription,
      ];
    case SelfServeSupportedAttachmentTypes.Audio:
      return [TaskType.TextCollection, TaskType.MultiModalTranscription];
    case SelfServeSupportedAttachmentTypes.Text:
      return isCoveredByHipaa
        ? [TaskType.TextCollection]
        : [TaskType.NamedEntityRecognition, TaskType.TextCollection];
    case SelfServeSupportedAttachmentTypes.Video:
      return [
        TaskType.VideoPlaybackAnnotation,
        TaskType.TextCollection,
        TaskType.MultiModalTranscription,
      ];
    case SelfServeSupportedAttachmentTypes.Pdf:
      return [TaskType.DocumentTranscription, TaskType.TextCollection];
    case SelfServeSupportedAttachmentTypes.DownloadableText:
      return isCoveredByHipaa
        ? [TaskType.TextCollection]
        : [TaskType.NamedEntityRecognition, TaskType.TextCollection];
    case SelfServeSupportedAttachmentTypes.Json:
      return [TaskType.TextCollection];
    // missing lidar data type?
    default:
      return null;
  }
};

export const useSelfserveCustomerLabelOnly = (type?: TaskType) => {
  const [value, setValue] = useState(!type);

  useEffect(() => {
    if (!type) {
      setValue(true);
    } else {
      setValue(false);
    }
  }, [type]);

  return value;
};

export const createParams = async (
  type: TaskType,
  asset: UploadedAsset,
  taxonomy: any,
  useSignedUrl: boolean,
  fetchAsset: any,
  fetchAssetPages: any,
  fetchAssetToTemplateAttachment?: any,
  project?: Project,
  projectTemplateId?: any,
) => {
  const attachmentType = uploadedAssetToAttachmentType(asset);
  const storageUrl = asset.storageUrl;
  const url = useSignedUrl
    ? asset.signedUrl ?? (await fetchAsset(asset._id, projectTemplateId))
    : storageUrl;

  const attachment: any = {};

  if (type === TaskType.DocumentTranscription) {
    if (attachmentType === 'pdf') {
      // for pdfs, we must convert pages into individual images
      attachment.attachments = await fetchAssetPages(asset._id, projectTemplateId);
      attachment.attachment_type = 'image';
    } else {
      attachment.attachments = [url];
    }
  } else if (type === TaskType.TextCollection) {
    const textCollectionAttachmentType =
      attachmentType === TextCollectionAttachmentType.Audio
        ? TextCollectionAttachmentType.TranscriptionAudio
        : attachmentType;
    if (fetchAssetToTemplateAttachment) {
      attachment.attachments = await fetchAssetToTemplateAttachment(asset._id, project);
    } else if (asset.mimeType === 'text/json') {
      const attachmentsJson = JSON.parse(asset.text || '[]');
      // Processing nested attachments.
      attachment.attachments = await Promise.all(
        attachmentsJson.map(async (attachment: any) => {
          if (!attachment.assetId) {
            return attachment;
          }
          return {
            type: attachment.type,
            content: await fetchAsset(attachment.assetId, projectTemplateId),
          };
        }),
      );
      // Stack attachments by default (wouldn't work for images anyway)
      attachment.force_stacked = true;
    } else {
      attachment.attachments = [
        {
          type: textCollectionAttachmentType,
          content: attachmentType === 'text' || attachmentType === 'website' ? asset.text : url,
        },
      ];
    }
  } else if (type === TaskType.NamedEntityRecognition) {
    if (attachmentType === TextCollectionAttachmentType.DownloadableText) {
      const response = await fetch(url);
      attachment.text = await response.text();
    } else {
      attachment.text = asset.text;
    }
  } else if (type === TaskType.VideoPlaybackAnnotation) {
    attachment.attachment_type = attachmentType;
    attachment.attachment = useSignedUrl ? url : await fetchAsset(asset._id, projectTemplateId);
    attachment.metadata = asset.metadata;
    attachment.frame_rate = taxonomy.frame_rate ?? 1;
  } else {
    attachment.attachment =
      attachmentType === 'text' || attachmentType === 'website' ? asset.text : url;
    attachment.attachment_type = attachmentType;
  }

  return _.pickBy({
    ...taxonomy,
    ...attachment,
  });
};

export const getProjectType = (inputKey: UseCaseType) => {
  switch (inputKey) {
    case UseCaseType.AV_IMAGE:
      return TaskType.ImageAnnotation;
    case UseCaseType.AC_IMAGE:
      return TaskType.ImageAnnotation;
    case UseCaseType.LOGI_IMAGE:
      return TaskType.ImageAnnotation;
    case UseCaseType.AV_VIDEO:
      return TaskType.VideoPlaybackAnnotation;
    case UseCaseType.ARVR_VIDEO:
      return TaskType.VideoPlaybackAnnotation;
    case UseCaseType.RESUME_DOCUMENTS:
      return TaskType.DocumentTranscription;
    case UseCaseType.INVOICE_DOCUMENTS:
      return TaskType.DocumentTranscription;
    case UseCaseType.TEXT_GEN:
      return TaskType.TextCollection;
    case UseCaseType.RELEVANCE:
      return TaskType.TextCollection;
    case UseCaseType.MOS_RANKING:
      return TaskType.TextCollection;
    case UseCaseType.AUDIO_TRANSCRIPTION:
      return TaskType.TextCollection;
    case UseCaseType.GENERAL_CLASS:
      return TaskType.TextCollection;
    case UseCaseType.NER:
      return TaskType.NamedEntityRecognition;
    default:
      return TaskType.ImageAnnotation;
  }
};

export const getDefaultPipelineForProject = (taskType: TaskType, inputKey?: UseCaseType) => {
  if (inputKey && inputKey === UseCaseType.TEXT_GEN) {
    return TaskPipelineName.FreeformTextCollectionTask;
  } else if (taskType === TaskType.Categorization) {
    return TaskPipelineName.ConsensusTask;
  } else {
    return TaskPipelineName.StandardTask;
  }
};

const randomIntegerString = (): string => Math.random().toString(36).substring(7);

export const getProjectName = (inputKey: UseCaseType) => {
  switch (inputKey) {
    case UseCaseType.AV_IMAGE:
      return 'AV Template Project ' + randomIntegerString();
    case UseCaseType.AC_IMAGE:
      return 'AC Template Project ' + randomIntegerString();
    case UseCaseType.LOGI_IMAGE:
      return 'Logistics Template Project ' + randomIntegerString();
    case UseCaseType.AV_VIDEO:
      return 'AV Video Template Project ' + randomIntegerString();
    case UseCaseType.ARVR_VIDEO:
      return 'AR/VR Video Template Project ' + randomIntegerString();
    case UseCaseType.RESUME_DOCUMENTS:
      return 'Resume Template Project ' + randomIntegerString();
    case UseCaseType.INVOICE_DOCUMENTS:
      return 'Invoice Template Project ' + randomIntegerString();
    case UseCaseType.TEXT_GEN:
      return 'Text Generation Template Project ' + randomIntegerString();
    case UseCaseType.RELEVANCE:
      return 'Relevance Template Project ' + randomIntegerString();
    case UseCaseType.MOS_RANKING:
      return 'MOS Ranking Template Project ' + randomIntegerString();
    case UseCaseType.AUDIO_TRANSCRIPTION:
      return 'Audit Transcription Template Project ' + randomIntegerString();
    case UseCaseType.GENERAL_CLASS:
      return 'General Classification Template Project ' + randomIntegerString();
    case UseCaseType.NER:
      return 'NER Template Project ' + randomIntegerString();
    default:
      return 'Template Project ' + randomIntegerString();
  }
};

export const keyToNameMap: Record<UseCaseType, string> = {
  [UseCaseType.WITHOUT_TEMPLATE]: 'Start without a template',
  [UseCaseType.AV_IMAGE]: 'autonomous vehicles',
  [UseCaseType.AC_IMAGE]: 'automated checkout',
  [UseCaseType.LOGI_IMAGE]: 'logistics',
  [UseCaseType.AV_VIDEO]: 'autonomous vehicles video',
  [UseCaseType.ARVR_VIDEO]: 'AR/VR video',
  [UseCaseType.RESUME_DOCUMENTS]: 'resume parsing',
  [UseCaseType.INVOICE_DOCUMENTS]: 'invoice processing',
  [UseCaseType.TEXT_GEN]: 'text generation',
  [UseCaseType.RELEVANCE]: 'search relevance',
  [UseCaseType.MOS_RANKING]: 'MOS ranking',
  [UseCaseType.AUDIO_TRANSCRIPTION]: 'audio transcription',
  [UseCaseType.GENERAL_CLASS]: 'general classification',
  [UseCaseType.NER]: 'name entity recognition',
};

export const getImageUrlByKey = (key: UseCaseType) => {
  switch (key) {
    case UseCaseType.AV_IMAGE:
      return 'https://files.readme.io/f2bbda6-Screen_Shot_2020-08-11_at_2.29.56_PM.png';
    case UseCaseType.AC_IMAGE:
      return 'https://static.scale.com/uploads/AC%20Template.png';
    case UseCaseType.LOGI_IMAGE:
      return 'https://static.remotasks.com/uploads/Logistics%20Template.png';
    case UseCaseType.TEXT_GEN:
      return 'https://files.readme.io/86ad6ab-TextCollection.png';
    case UseCaseType.NER:
      return 'https://files.readme.io/4e61c29-Named_Entity_Recognition_.png';
    case UseCaseType.GENERAL_CLASS:
      return 'https://files.readme.io/e66c49f-Categorization.png';
    default:
      return null;
  }
};

export const getProjectState = ({
  regularBatchExists,
  hasEnoughQualityTasks,
  calibrationBatchExists,
  uploadedAssetsExist,
  taxonomy,
  instruction,
  numAudited,
}: $FIXME) => {
  if (regularBatchExists) return projectStates.regular_batch_launched;
  if (hasEnoughQualityTasks) return projectStates.launch_regular_batch;
  if (numAudited) return projectStates.quality_tasks;
  if (calibrationBatchExists) return projectStates.calibration_review;
  if (instruction?.instructionObjectId) return projectStates.calibration_create;
  if (taxonomy) return projectStates.instruction_write;
  if (uploadedAssetsExist) return projectStates.taxonomy_define;
  return projectStates.data_upload;
};

export const getProjectState2 = ({
  regularBatchExists,
  hasEnoughQualityTasks,
  calibrationBatchExists,
  numAudited,
}: $FIXME) => {
  if (regularBatchExists) return projectStates2.regular_batch_launched;
  if (hasEnoughQualityTasks) return projectStates2.launch_regular_batch;
  if (numAudited) return projectStates2.quality_tasks;
  if (calibrationBatchExists) return projectStates2.calibration_review;
  return projectStates2.calibration_create;
};

export const getUrlBase = (base: string) => {
  const re = /\/([^/]*).*$/;
  const [, product] = base.match(re) as unknown as [null, string];

  return product;
};

export const getDashboardPageTypeFromProject = (project?: Project): DashboardPageType => {
  if (!project) {
    return getDashboardPageTypeFromPathName(window.location.pathname);
  }

  if (isStudioProject(project)) {
    return DashboardPageType.Studio;
  } else if (isRapidProject(project)) {
    return DashboardPageType.Rapid;
  }
  return DashboardPageType.Pro;
};

function getDashboardPageTypeFromPathName(pathname: string): DashboardPageType {
  if (pathname.startsWith(`/${DashboardPageType.Studio}`)) {
    return DashboardPageType.Studio;
  } else if (pathname.startsWith(`/${DashboardPageType.Pro}`)) {
    return DashboardPageType.Pro;
  } else if (pathname.startsWith(`/${DashboardPageType.Rapid}`)) {
    return DashboardPageType.Rapid;
  }
  return DashboardPageType.Classic;
}

export const projectStates = Object.freeze({
  regular_batch_launched: {
    progress: 100,
  },
  launch_regular_batch: {
    progress: 90,
    text: 'Launch production batch',
    to: '/batches',
  },
  quality_tasks: {
    progress: 70,
    text: 'Audit & create quality tasks',
    to: '/batches',
  },
  calibration_review: {
    progress: 55,
    text: 'Review feedback from calibration',
    to: '/batches',
  },
  calibration_create: {
    progress: 45,
    text: 'Create calibration batch',
    to: '/batches',
  },
  instruction_write: {
    progress: 30,
    text: 'Write instructions',
    to: '/definition',
  },
  taxonomy_define: {
    progress: 15,
    text: 'Define taxonomy',
    to: '/definition',
  },
  data_upload: {
    progress: 0,
    text: 'Upload data',
    to: '/definition',
  },
});

export const projectStates2 = Object.freeze({
  regular_batch_launched: {
    progress: 100,
  },
  launch_regular_batch: {
    progress: 70,
    text: 'Launch production batch',
    to: '/batches',
  },
  quality_tasks: {
    progress: 50,
    text: 'Audit & create quality tasks',
    to: '/batches',
  },
  calibration_review: {
    progress: 10,
    text: 'Review feedback from calibration',
    to: '/batches',
  },
  calibration_create: {
    progress: 0,
    text: 'Create calibration batch',
    to: '/batches',
  },
});

export type UploadSource = 'local' | 'previous_project' | 's3' | 'gcp' | 'azure' | 'url';
export enum UploadSourceLabel {
  local = 'Local',
  previous_project = 'Previous Project',
  s3 = 'Amazon S3',
  gcp = 'Google Cloud Platform',
  azure = 'Microsoft Azure',
  url = 'URL',
}

export const uploadSourceToIcon: Record<UploadSourceLabel, string> = {
  [UploadSourceLabel.local]: 'file',
  [UploadSourceLabel.previous_project]: 'bolt',
  [UploadSourceLabel.s3]: 'cloud',
  [UploadSourceLabel.gcp]: 'cloud',
  [UploadSourceLabel.azure]: 'cloud',
  [UploadSourceLabel.url]: 'link',
};

export const useRapidHistory = () => {
  const history = useHistory();

  function redirect(
    path: string,
    opts?: {
      projectId?: string;
      clearProject?: boolean;
      additionalArgs?: Record<string, string | undefined>;
    },
  ) {
    const { project, spoof } = queryString.parse(history.location.search);
    const search = queryString.stringify({
      project: opts?.clearProject ? undefined : opts?.projectId || project,
      spoof,
      ...opts?.additionalArgs,
    });

    let urlBase;

    if (path.startsWith(`/${DashboardPageType.Rapid}/`)) {
      urlBase = DashboardPageType.Rapid;
      path = path.replace(`/${urlBase}`, '');
    } else if (path.startsWith(`/${DashboardPageType.Studio}/`)) {
      urlBase = DashboardPageType.Studio;
      path = path.replace(`/${urlBase}`, '');
    } else if (path.startsWith(`/${DashboardPageType.Pro}/`)) {
      urlBase = DashboardPageType.Pro;
      path = path.replace(`/${urlBase}`, '');
    } else {
      urlBase = getUrlBase(history.location.pathname);
    }

    history.push({
      pathname: `/${urlBase}${path}`,
      search,
    });
  }

  function convertPathBetweenRapidStudio(toRapid: boolean) {
    history.replace({
      pathname: toRapid
        ? history.location.pathname.replace('/studio', '/rapid')
        : history.location.pathname.replace('/rapid', '/studio'),
      search: history.location.search,
    });
  }

  function urlWithQuerystring(url: string, projectId?: string, clearBatchQuery?: boolean) {
    const { spoof, project } = queryString.parse(history.location.search);
    if (projectId !== undefined) {
      const search = projectId
        ? queryString.stringify({ project: projectId, spoof })
        : queryString.stringify({ spoof });
      return `/${getUrlBase(history.location.pathname)}/${url}?${search}`;
    }
    if (clearBatchQuery) {
      const search = queryString.stringify({ project, spoof });
      return `/${getUrlBase(history.location.pathname)}/${url}?${search}`;
    }

    return `/${getUrlBase(history.location.pathname)}/${url}${history.location.search}`;
  }

  function isRapidUrl() {
    return getDashboardPageTypeFromUrlPath() === DashboardPageType.Rapid;
  }

  function isStudioUrl() {
    return window.location.pathname.includes('studio');
  }

  function canViewOldModelEval() {
    return (
      window.location.pathname.includes('modeleval') &&
      window.location.search.includes('spoof') &&
      window.location.search.includes('old')
    );
  }
  function getDashboardPageTypeFromUrlPath(): DashboardPageType {
    return getDashboardPageTypeFromPathName(history.location.pathname);
  }

  return {
    redirect,
    isRapidUrl,
    isStudioUrl,
    canViewOldModelEval,
    getDashboardPageTypeFromUrlPath,
    convertPathBetweenRapidStudio,
    urlWithQuerystring,
  };
};

export const useIsNewUser = (user: IUserJson | null): { isNewUser: boolean } => {
  const [isNewUser, setIsNewUser] = useState(false);

  const { isRapidUrl, isStudioUrl } = useRapidHistory();

  useEffect(() => {
    if (user) {
      const request = async () => {
        if (user.teamRole === 'admin') {
          const { numProjects } = await API.selfserve.fetchNumProjects();
          // TODO: change this to !user.rapidCustomer after backfill
          const showOnboarding =
            (isRapidUrl() && numProjects === 0 && !user.firstName) || // show if user has no projects and has no first name
            (isStudioUrl() && (!user.company || !user.firstName)); // each user needs company and name for studio

          if (showOnboarding) {
            setIsNewUser(true);
          }
        }
        if (user.teamRole !== 'admin' && isStudioUrl()) {
          setIsNewUser(!user.firstName);
        }
      };

      request();
    }
  }, [user]);

  return { isNewUser };
};
